import { isEventShaded } from '@/features/events/lib/eventKind.lib';
import type { PageExperience } from '@/features/experiences/types';
import { useActiveDate } from '@/features/planner/hooks/useActiveDate';
import { Box, Flex, Text } from '@/ui';
import type { DayContextType } from '@/ui/Calendar/DayContext';
import { FONT_CALENDAR_HEADING } from '../../../css/customFonts';
import { getStyleById } from '../../../theme';
import { EventIndicator } from '../../shared/EventIndicator';
import { toIndicatorRow } from '../../shared/eventIndicator.lib';
import type { GroupData } from '../types';
import { isWeekend, toMonthKey } from '../util.lib';

type Props = {
  dayContext: DayContextType;
  box: GroupData;
  experience: PageExperience;
};

export const MobileDayCell = ({ dayContext, box, experience }: Props) => {
  const [activeDate, setActiveDate] = useActiveDate();
  const theme = getStyleById(experience.styleId ?? undefined);

  const monthKey = toMonthKey({
    month: dayContext.month,
    year: dayContext.year,
  });
  const dayMeasureKey = `day-${monthKey}-${dayContext.day}`;
  const dayHeaderMeasureKey = `day-header-${monthKey}-${dayContext.day}`;
  const dayContentMeasureKey = `day-content-${monthKey}-${dayContext.day}`;

  const isActive = activeDate ? activeDate.hasSame(box.date, 'day') : false;

  const hasBlockingEvent = box.events.some(isEventShaded);

  const { events: indicatorEvents, overflowCount } = toIndicatorRow(
    experience,
    box.events
  );

  const borderColor = isActive
    ? theme.accentColor || theme.textColor
    : theme.neutral6 || theme.borderColor;

  return (
    <Flex
      as="button"
      border="solid 1px"
      borderColor={borderColor}
      borderRadius="0.25em"
      boxSizing="border-box"
      color="inherit"
      cursor="pointer"
      data-measure={dayMeasureKey}
      flexDirection="column"
      justifyContent="space-around"
      minH={{ base: '4em', sm: '5.5em' }}
      minW={0}
      overflow="hidden"
      p={0}
      type="button"
      w="100%"
      background={
        (hasBlockingEvent ? theme.neutral9 : theme.neutral10) ||
        theme.backgroundColor
      }
      sx={{
        appearance: 'none',
        font: 'inherit',
        textAlign: 'inherit',
      }}
      onClick={() => setActiveDate(box.date)}
    >
      <Box
        data-measure={dayHeaderMeasureKey}
        fontFamily={FONT_CALENDAR_HEADING}
        fontSize="1em"
        fontWeight="600"
        pb="0.25em"
        pt={{ base: '0.6em', sm: '1.25em' }}
        px="0.5em"
        textAlign="center"
        w="100%"
        color={
          isWeekend(dayContext.dayOfWeek) ? theme.neutral5 : theme.textColor
        }
      >
        {dayContext.day}
      </Box>
      <Flex
        alignItems="center"
        data-measure={dayContentMeasureKey}
        flexDirection="row"
        flexGrow={1}
        gap="0.1em"
        justifyContent="center"
        overflow="hidden"
        px="0"
        py="0"
        w="100%"
      >
        {indicatorEvents.map((event) => {
          const key = `event-${event.id}-${box.date.toISODate()}`;
          return (
            <Box data-measure={key} key={key}>
              <EventIndicator event={event} experience={experience} />
            </Box>
          );
        })}
        {overflowCount > 0 && (
          <Text
            color={theme.neutral5 || theme.textColor}
            fontSize="0.45em"
            fontWeight="600"
            lineHeight="1"
            whiteSpace="nowrap"
          >
            +{overflowCount}
          </Text>
        )}
      </Flex>
    </Flex>
  );
};
